import { Box, Button, Tooltip, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import Checkbox from 'components/checkbox';
import DatePicker from 'components/date-picker';
import FilterSidebar from 'components/filter-sidebar';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { saveState } from 'utils/storage';
import { getZones } from 'views/production-systems/api/productionSystems';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import Filters from 'views/production-systems/features/farm-setup/types/farm-setup-filters.type';
import { Pond } from 'views/production-systems/features/farm-setup/types/pond.type';
import { RegistrationDateType } from 'views/production-systems/types/pond.type';

const DateButtonArrow = styled(Button)(({ theme }) => ({
  height: 30,
  minHeight: 30,
  borderRadius: 8,
  color: theme.palette.darkBlue.main,
  background: theme.palette.common.white,
  '&:hover': {
    color: theme.palette.darkBlue.main,
    background: theme.palette.common.white,
  },
  '&.Mui-disabled': {
    color: theme.palette.darkBlue.main,
    background: theme.palette.darkBlue.o25,
  },
}));

const defaultValues: Filters = {
  zoneId: '',
  searchValue: '',
  registrationDate: dayjs(),
  showActivePonds: true,
  showInactivePonds: true,
  enableKg: true,
};

type SortablePond = Pond & {
  status: boolean;
};

const PAGE_NAME_ID = 'productionSystems.farmSetup';

type Props = {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  renderPonds: SortablePond[];
};

const FarmSetupFilters: React.FC<Props> = ({ renderPonds, filters, setFilters }) => {
  const intl = useIntl();
  const params = useParams<{ globalSiteId: string }>();
  const { changeGlobalSiteId, registrationSetupDate, changeRegistrationDate, selectedPonds, setSelectedPonds } = usePondsState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: zones } = useGet(['getZones', params.globalSiteId], ({ queryKey }) => getZones(String(queryKey[1])), {
    select: (data) =>
      data.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
        })
      ),
    enabled: !!params.globalSiteId,
  });

  const addDate = () => changeRegistrationDate(registrationSetupDate.add(1, 'days'), RegistrationDateType.setup);

  const subtractDate = () => changeRegistrationDate(registrationSetupDate.subtract(1, 'days'), RegistrationDateType.setup);

  const resetStates = () => {
    updateFilters(defaultValues);
  };

  const updateFilters = (newFilters: Filters) => {
    setFilters(newFilters);
    saveState({ key: 'farmSetupFilters', state: newFilters });
  };

  useEffect(() => {
    if (params.globalSiteId) {
      changeGlobalSiteId(params.globalSiteId);
    } else {
      changeGlobalSiteId('');
      resetStates();
    }
  }, [params.globalSiteId]);

  return (
    <>
      <FilterSidebar
        showClearFilter={!!filters.zoneId || !!filters.searchValue || !filters.registrationDate}
        clearFilters={() => {
          resetStates();
        }}>
        <Box width={'100%'} display={'flex'} gap={4} flexDirection={isMobile ? 'column' : 'row'} justifyContent={'flex-end'}>
          <Select
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.zone` })}
            value={filters.zoneId}
            onChange={(event) => {
              let newFilters = { ...filters, zoneId: String(event.target.value) };
              updateFilters(newFilters);
              setSelectedPonds([]);
            }}>
            {zones?.map((zone) => (
              <RadioSelectItem key={zone.id} value={zone.id}>
                {zone.name}
              </RadioSelectItem>
            ))}
          </Select>
          <DatePicker
            disableFuture
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.registrationDate` })}
            value={registrationSetupDate}
            format={"DD/MM/YYYY"}
            onChange={(date) => {
              changeRegistrationDate(date, RegistrationDateType.setup);
            }}
          />
          <TextField
            value={filters.searchValue}
            label={intl.formatMessage({ id: 'general.search' })}
            onChange={(event) => {
              let newFilters = { ...filters, searchValue: event.target.value as string };
              updateFilters(newFilters);
            }}
            endAdornment={<Search size={18} />}
          />
        </Box>
        <Box display={'flex'} flexDirection={'row'} gap={4} justifyContent={'flex-end'} alignItems={'center'}>
          <Switch
            sx={{ textAlign: 'right', width: { xs: '100%', md: 'auto' } }}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.poundsOrKg` })}
            value={filters.enableKg}
            onChange={() => {
              let newFilters = { ...filters, enableKg: !filters.enableKg };
              updateFilters(newFilters);
            }}
          />
          <Switch
            sx={{ textAlign: 'right', width: { xs: '100%', md: 'auto' } }}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showActive` })}
            value={filters.showActivePonds}
            onChange={() => {
              let newFilters = { ...filters, showActivePonds: !filters.showActivePonds };
              updateFilters(newFilters);
            }}
          />
          <Switch
            sx={{ textAlign: 'right', width: { xs: '100%', md: 'auto' } }}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showInactive` })}
            value={filters.showInactivePonds}
            onChange={() => {
              let newFilters = { ...filters, showInactivePonds: !filters.showInactivePonds };
              updateFilters(newFilters);
            }}
          />
        </Box>
      </FilterSidebar>

      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        color={theme.palette.common.white}
        flexDirection={isMobile ? 'column-reverse' : 'row'}>
        <Box display={'flex'} alignItems={'center'} justifyContent={'flex-start'} width={200}>
          <Checkbox
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.selectAll` })}
            checked={selectedPonds.length === renderPonds.length}
            onChange={() => {
              selectedPonds.length === renderPonds.length
                ? setSelectedPonds([])
                : setSelectedPonds(renderPonds.map((pond) => pond.pondId));
            }}
          />
        </Box>
        <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={4} color={theme.palette.common.white}>
          <Typography>
            <FormattedMessage id={`${PAGE_NAME_ID}.filters.registrationDate`} />
            {`: `}
            <span>{dayjs(registrationSetupDate).format('DD/MM/YYYY')}</span>
          </Typography>
          <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.previousDay` })}>
            <DateButtonArrow size="small" disableRipple onClick={subtractDate}>
              <ChevronLeft size={18} />
            </DateButtonArrow>
          </Tooltip>
          <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.nextDay` })}>
            <DateButtonArrow disableRipple disabled={registrationSetupDate.isSameOrAfter(dayjs(), 'day')} onClick={addDate}>
              <ChevronRight size={18} />
            </DateButtonArrow>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};

export default FarmSetupFilters;
